import moment from 'moment';
import React from 'react';
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  usePagination,
} from 'react-table';
import styled from 'styled-components';

import useAPI from '@hooks/useAPI';
import Button from '@shared/Button';
import Spinner from '@shared/Spinner';
import StatusButton from '@shared/StatusButton';

const listAccessor = ({ list_name, updated_at }) => ({ list_name, updated_at });

const Container = styled.div`
  text-align: center;
  width: 100%;
  p {
    margin: 0 0 35px 0px;
  }
`;

const FullWidthContainer = styled.div`
  width: 100%;
`;
const FullWidthTable = styled.table`
  width: 100%;
  th {
  text-align: left;

  }
`;

const TableBodyContainer = styled.tbody``;
const Row = styled.tr`
  :not(:last-child) {
    border-bottom: ${props => props.theme.borderMd};
  }
`;
const CellContainer = styled.td`
  padding: 15px 0px 10px 0px;
  vertical-align: middle;
`;

const AboveLabel = styled.span`
  font-size: ${props => props.theme.font.fontSm};
  color: ${props => props.theme.fontTertiary};
`;

const Label = styled.span`
  line-height: 24px;
  color: ${props => props.theme.primary};
`;

const FloatRight = styled.span`
  text-align: right;
  float: right;
`;

const PaginationStyle = styled.div`
  margin: 40px 20%;
  // border: 2px solid red;
  max-width: 700px;
  width: 700px;

  select,
  span {
    margin: 0 10px;
  }

  select {
    // border: none;
    outline: none;
    border-radius 2px;
  }

  button {
    margin: 0 10px;
    font-size: 1.03rem;
    padding: 7px 7px;
    color: #7b9095;
    border: none;
    outline: none;
    border-radius: 10px;
    background: #c6d2db;
    // transition: all 0.2s;
  }

  .btn__next:hover {
    color: ${props => props.canNextPage && 'white'};
    background: ${props => props.canNextPage && '#7b9095'};
    // transform: ${props => props.canNextPage && 'scale(1.04)'};
  }

  .btn__prev:hover {
    color: ${props => props.canPreviousPage && 'white'};
    background: ${props => props.canPreviousPage && '#7b9095'};
  }
`;

export default function ListsTable() {
  const listsRequest = useAPI('get', 'ScreenListApi', 'ingestionstatus');
  const onClickReload = listsRequest.invalidateCache;

  let data = listsRequest?.response?.results;
  const hasError = listsRequest?.error;
  const isLoading = listsRequest.isLoading;
  if (!Array.isArray(data)) {
    data = [];
  }
  const [previousFilterValue, setPreviousFilterValue] = React.useState('');

  if (isLoading) {
    return (
      <FullWidthContainer>
        <Spinner center />
      </FullWidthContainer>
    );
  }

  if (hasError) {
    return (
      <Container>
        <p>We countered a problem while fetching lists</p>
        <p> {hasError.toString()}</p>
        <Button onClick={onClickReload}>Retry</Button>
      </Container>
    );
  }

  if (data && data.length) {
    return <TableContent data={data} />;
  }

  return (
    <FullWidthContainer>
      <Spinner center />
    </FullWidthContainer>
  );
}

const TableContent = ({ data }) => {
  const columns = React.useMemo(
    () => [
      {
        Header: 'List Name',
        Cell: ({ cell: { value } }) => (
          <span>
            <AboveLabel>
              Last Updated {moment(value.updated_at).format('MM/DD/YYYY')}
              <br />
            </AboveLabel>
            <Label>{value.list_name}</Label>
          </span>
        ),
        accessor: listAccessor,
      },
      {
        Header: 'created_at',
        id: 'created_at',
        accessor: 'created_at',
      },
      {
        Header: 'version',
        id: 'version',
        accessor: 'version',
      },
      {
        Header: 'Status',
        id: 'status',
        Cell: ({ cell: { value } }) => <StatusButton status={value} />,
        accessor: 'list_status',
      },
    ],
    []
  );

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );
  const defaultColumn = React.useMemo(
    () => ({
      Filter: undefined,
    }),
    []
  );

  const initialState = {
    globalFilter: '',
    sortBy: [{ id: 'created_at', desc: true }],
    pageSize: 10,
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    state,
    rows,
    prepareRow,
    page,
    pageCount,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    ...other
  } = useTable(
    { columns, data: React.useMemo(() => data, [data]), initialState },
    useSortBy,
    usePagination
  );

  // TODO fix typedefs and avoid this hack
  const {
    setGlobalFilter,
    globalFilteredRows,
    preGlobalFilteredRows,
    sortedRows,
  } = other;
  const { globalFilter, pageIndex, pageSize } = state;

  return (
    <>
      <FullWidthTable {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr>
              {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                    </span>
                  </th>
              ))}
            </tr>
          ))}
        </thead>
        <TableBodyContainer {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <Row {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <CellContainer {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </CellContainer>
                  );
                })}
              </Row>
            );
          })}
        </TableBodyContainer>
      </FullWidthTable>
      <PaginationStyle
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
      >
        <span>
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>{' '}
        </span>
        <select
          value={pageSize}
          onChange={event => setPageSize(Number(event.target.value))}
        >
          {[10, 20, 50].map(pageSize => {
            return (
              <option value={pageSize} key={pageSize}>
                Show {pageSize}
              </option>
            );
          })}
        </select>
        <button
          className='btn__prev'
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
        >
          Previous Page
        </button>
        <button
          className='btn__next'
          onClick={() => nextPage()}
          disabled={!canNextPage}
        >
          Next Page
        </button>
      </PaginationStyle>
    </>
  );
};
